import { useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Building2, Key, MapPin, Calculator, TrendingUp, ShieldCheck, ArrowRight, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";

export interface ServiceDetail {
  slug: string;
  title: string;
  shortDesc: string;
  icon: LucideIcon;
  fullDesc: string[];
  features: string[];
}

const SERVICES_DATA: Record<string, ServiceDetail> = {
  "gayrimenkul-danismanligi": {
    slug: "gayrimenkul-danismanligi",
    title: "Adana Gayrimenkul Danışmanlığı",
    shortDesc: "Seyhan, Çukurova ve Gürselpaşa bölgesinde konut alım, satım ve kiralama süreçlerinde profesyonel danışmanlık.",
    icon: Building2,
    fullDesc: [
      "Corner Gayrimenkul, Adana'da ev alırken veya kiralarken en doğru kararı vermeniz için eksiksiz danışmanlık hizmeti sunar.",
      "Gürselpaşa, Pınar, Güzelyalı ve Mahfesığmaz gibi nitelikli mahallelerdeki güncel portföyleri ve tapu süreçlerini sizin adınıza yönetiyoruz."
    ],
    features: ["Piyasa Emsal Analizi", "Tapu Takip Hizmetleri", "Kredi Uygunluk Sorgulama", "Hukuki Sözleşme Desteği"]
  },
  "yatirim-danismanligi": {
    slug: "yatirim-danismanligi",
    title: "Gayrimenkul Yatırım Danışmanlığı",
    shortDesc: "Yüksek prim potansiyeline ve hızlı kira getirisine sahip Adana gayrimenkul yatırım fırsatları analizi.",
    icon: TrendingUp,
    fullDesc: [
      "Birikimlerinizi Adana'nın en çok değer kazanan gelişme akslarında değerlendirmeniz için veriye dayalı yatırım raporları sunuyoruz.",
      "Sarıçam Çarkıpare, Seyhan Gürselpaşa ve Çukurova caddelerindeki yüksek amortismanlı gayrimenkul fırsatlarını listeliyoruz."
    ],
    features: ["Amortisman Süresi Hesaplama", "Yıllık Değer Artış Tahmini", "Kira Getirisi Senaryoları", "Portföy Çeşitlendirmesi"]
  },
  "mulk-degerleme": {
    slug: "mulk-degerleme",
    title: "Adana Mülk Değerleme & Ekspertiz",
    shortDesc: "Evinizin, arsanızın veya dükkanınızın gerçek piyasa değerini güncel emsal verileriyle tespit etme.",
    icon: Calculator,
    fullDesc: [
      "Gayrimenkulünüzün satış veya kiralama değerini yanlış tahmin etmek zaman ve nakit kaybına sebep olur.",
      "Corner Gayrimenkul olarak gerçekleşmiş satış verileriyle mülkünüz için ücretsiz ekspertiz raporu hazırlıyoruz."
    ],
    features: ["Bölgesel Emsal Kıyaslaması", "Metrekare Fiyat Tespiti", "Ücretsiz Değerleme Raporu", "Hızlı Pazarlama Tavsiyeleri"]
  },
  "konut-satis-danismanligi": {
    slug: "konut-satis-danismanligi",
    title: "Konut Satış Danışmanlığı",
    shortDesc: "Gayrimenkulünüzü en doğru fiyattan ve en kısa sürede nitelikli alıcılarla buluşturma hizmeti.",
    icon: ShieldCheck,
    fullDesc: [
      "Mülkünüzü Corner web sitemizde, Sahibinden mağazamızda ve dijital pazarlama ağımızda öne çıkararak satış sürecini hızlandırıyoruz."
    ],
    features: ["Profesyonel Fotoğraf Çekimi", "Hedef Kitleye Dijital Reklam", "Nitelikli Alıcı Elemesi", "Şeffaf Pazarlık Yönetimi"]
  }
};

export default function ServicesPage() {
  const { slug } = useParams<{ slug?: string }>();

  if (slug && SERVICES_DATA[slug]) {
    const service = SERVICES_DATA[slug];

    return (
      <div className="min-h-screen bg-background flex flex-col justify-between">
        <SEOHead
          title={`${service.title} | Corner Gayrimenkul`}
          description={service.shortDesc}
          canonicalPath={`/hizmetler/${service.slug}`}
        />
        <HeaderNavigation />

        <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
          <Breadcrumbs
            items={[
              { label: "Hizmetlerimiz", url: "/hizmetler" },
              { label: service.title }
            ]}
          />

          <article className="space-y-6 my-6">
            <div className="space-y-3">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
                Kurumsal Hizmetlerimiz
              </span>
              <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
                {service.title}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {service.shortDesc}
              </p>
            </div>

            <div className="bg-card p-6 rounded-2xl border border-border space-y-4">
              <h3 className="font-extrabold text-xl text-foreground">Hizmet Detayları</h3>
              <div className="space-y-3 text-muted-foreground text-base leading-relaxed">
                {service.fullDesc.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-extrabold text-xl text-foreground mb-4">Hizmet Kapsamı & Avantajları</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.features.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary inline-block"></span>
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-8 rounded-2xl border border-primary/20 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center sm:text-left">
                <h3 className="text-xl font-bold text-foreground">Bu Hizmet Hakkında Bilgi Alın</h3>
                <p className="text-sm text-muted-foreground">Uzman gayrimenkul danışmanımız sorularınızı yanıtlasın.</p>
              </div>
              <Button asChild size="lg" className="font-bold shrink-0">
                <a href="https://wa.me/905335487801?text=Merhaba,%20hizmetleriniz%20hakkında%20bilgi%20almak%20istiyorum." target="_blank" rel="noreferrer">
                  <MessageSquare className="h-4 w-4 mr-2" /> Danışmanla Görüşün
                </a>
              </Button>
            </div>
          </article>
        </main>

        <FooterSEO />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Hizmetlerimiz | Corner Gayrimenkul Adana"
        description="Corner Gayrimenkul Adana gayrimenkul danışmanlığı, mülk değerleme, satış danışmanlığı ve yatırım danışmanlığı hizmetleri."
        canonicalPath="/hizmetler"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Hizmetlerimiz" }]} />

        <div className="mb-12 text-center max-w-3xl mx-auto">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
            Kurumsal Gayrimenkul Çözümleri
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight my-4">
            Corner Gayrimenkul Hizmetleri
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Adana Seyhan ve Çukurova bölgelerindeki gayrimenkul alım, satım, kiralama ve yatırım süreçlerinizde uzman desteği.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
          {Object.values(SERVICES_DATA).map((srv) => (
            <Card key={srv.slug} className="p-8 bg-card border border-border/80 hover:border-primary transition-all shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold mb-4">
                  <srv.icon className="h-6 w-6" />
                </div>
                <h3 className="font-extrabold text-2xl text-foreground mb-3 hover:text-primary transition-colors">
                  <Link to={`/hizmetler/${srv.slug}`}>
                    {srv.title}
                  </Link>
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed mb-6">
                  {srv.shortDesc}
                </p>
              </div>

              <Link to={`/hizmetler/${srv.slug}`} className="text-sm font-bold text-primary hover:underline flex items-center gap-1">
                Hizmet Detayını İncele <ArrowRight className="h-4 w-4" />
              </Link>
            </Card>
          ))}
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
